/**
 * Export to CSV control for the Manage Returns list; mirrors legacy report button behavior and Mixpanel tracking.
 */
import "./rma-list-export-report.scss";
import { observer } from "mobx-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { IReportFiltersType, REPORT_TYPES } from "../../../../../services/api/report-service";
import ReportStore from "../../../../../stores/report-store";
import IconExportCsv from "../../../../../components/icons/icon-export-csv/icon-export-csv";
import RoundLoader, { RoundLoaderType } from "../../../../../components/loaders/round-loader/round-loader";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import RmaListExportReportExpanded from "../rma-list-export-report-expanded/rma-list-export-report-expanded";
import IconDropDownSmallArrow from "../../../../../components/icons/icon-drop-down-small-arrow/icon-drop-down-small-arrow";
import IconDropDownUpSmallArrow from "../../../../../components/icons/icon-drop-down-up-small-arrow/icon-drop-down-up-small-arrow";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";

const ICON_COLOR_DISABLED = "var(--color-primary-3-50)";
const ICON_COLOR = "var(--color-accent-1)";
const EXPANDED_CHEVRON_COLOR = "var(--color-neutral-1-white)";

interface IReportProps {
    getReportFilters?: () => IReportFiltersType;
    disabled?: boolean;
}

const RmaListExportReport: React.FC<IReportProps> = observer(({ getReportFilters, disabled = false }) => {
    const store = useMemo<ReportStore>(() => new ReportStore(REPORT_TYPES.RETURNS, getReportFilters), [getReportFilters]);
    const [isExpanded, setIsExpanded] = useState(false);
    const buttonRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        store.load();
    }, [store]);

    const isReportGenerating = store.isGenerating;
    const isReportInitialLoad = store.isLoading;
    const isNonInteractive = disabled || isReportGenerating || isReportInitialLoad;
    const isMuted = disabled || isReportGenerating;
    const iconColor = isMuted ? ICON_COLOR_DISABLED : ICON_COLOR;
    const fontColor = isMuted ? FONT_COLOR.PRIMARY3_50 : FONT_COLOR.ACCENT1;

    const onClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        if (isNonInteractive) {
            return;
        }

        e.stopPropagation();
        rmsMixpanelStore.trackEvent("d&_report");
        if (!store.lastGenerated) {
            store.generateReport();
        } else {
            setIsExpanded(!isExpanded);
        }
    };

    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";

    const triggerClassName = [
        "rma-list-export-report__trigger",
        disabled && "disabled",
        isReportGenerating && "loading",
    ].filter(Boolean).join(" ");

    return (
        <div className="rma-list-export-report">
            <span className={triggerClassName} ref={buttonRef} onClick={onClick}>
                <span className="rma-list-export-report__trigger-title">
                    <span className="rma-list-export-report__icon-slot">
                        {isReportGenerating ? (
                            <RoundLoader type={RoundLoaderType.Dark} />
                        ) : (
                            <IconExportCsv color={iconColor} />
                        )}
                    </span>
                    <FontBody1 variant={[FONT_VARIANT.BOLD, FONT_VARIANT.UPPERCASE]} color={fontColor}>
                        {getTranslation("EXPORT_TO_CSV", scope)}
                    </FontBody1>
                </span>
                {store.lastGenerated && (
                    <span className={`rma-list-export-report__chevron${isExpanded ? " expanded" : ""}`}>
                        {isExpanded ? (
                            <IconDropDownUpSmallArrow color={EXPANDED_CHEVRON_COLOR} />
                        ) : (
                            <IconDropDownSmallArrow color={iconColor} />
                        )}
                    </span>
                )}
            </span>
            {isExpanded && store.lastGenerated && (
                <RmaListExportReportExpanded
                    refElement={buttonRef.current}
                    isGeneratingReport={store.isGenerating}
                    onClick={() => setIsExpanded(false)}
                    onGenerateNewClick={() => {
                        setIsExpanded(false);
                        store.generateReport();
                    }}
                    report={store.lastGenerated}
                    reportStore={store}
                />
            )}
        </div>
    );
});

export default RmaListExportReport;
